import React from 'react';
import { Container, Title, Text, Stack, Anchor, Paper, List } from '@mantine/core';

/**
 * Privacy Policy Page
 *
 * Displays the privacy policy for MentionedMe.
 *
 * SESSION_210: Initial implementation
 * SESSION_224: Added MarketingHeader for navigation
 * SPRINT_039: Updated for production readiness with MentionedMe-specific details
 * Sprint 041: Removed MarketingHeader/Footer (now provided by PublicLayout)
 */
export const PrivacyPage: React.FC = (): React.ReactElement => {
  const lastUpdated = 'February 2026';

  return (
    <Container size="md" py="xl">
        <Paper p="xl" withBorder>
          <Stack gap="lg">
            <Title order={1}>Privacy Policy</Title>
            <Text c="dimmed" size="sm">Last updated: {lastUpdated}</Text>

            <Stack gap="md">
              <section>
                <Title order={2} size="h3" mb="xs">1. Introduction</Title>
                <Text>
                  MentionedMe (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is committed to protecting your privacy.
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information
                  when you use our platform and services.
                </Text>
                <Text mt="xs">
                  MentionedMe is a social media monitoring service that helps business owners discover when their
                  businesses are mentioned in public discussions on Reddit. We analyze publicly available Reddit
                  comments to identify potential business mentions and notify business owners of these mentions.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">2. Information We Collect</Title>
                <Text mb="xs">We collect the following types of information:</Text>
                <List>
                  <List.Item>
                    <strong>Account Information:</strong> Name, email address, phone number, and business details
                    when you create an account or claim a business.
                  </List.Item>
                  <List.Item>
                    <strong>Payment Information:</strong> Billing address and payment method details processed
                    securely through our payment provider, Stripe. We do not store your full credit card number.
                  </List.Item>
                  <List.Item>
                    <strong>Usage Data:</strong> Information about how you interact with our platform, including
                    pages visited, features used, actions taken, and IP addresses for security purposes.
                  </List.Item>
                  <List.Item>
                    <strong>Business Data:</strong> Information about your business that you provide, including
                    business name, address, website, and category. We also import publicly available business
                    information from OpenStreetMap.
                  </List.Item>
                  <List.Item>
                    <strong>Social Media Data:</strong> We collect and analyze publicly available Reddit comments
                    that may mention businesses. This includes Reddit post titles, comment text, subreddit names,
                    and Reddit usernames of public posts. We do not collect private messages or non-public content.
                  </List.Item>
                </List>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">3. How We Use Your Information</Title>
                <Text mb="xs">We use the information we collect to:</Text>
                <List>
                  <List.Item>Provide, maintain, and improve our services</List.Item>
                  <List.Item>Process transactions and send related information</List.Item>
                  <List.Item>Send notifications about mentions of your business</List.Item>
                  <List.Item>Respond to your comments, questions, and requests</List.Item>
                  <List.Item>Monitor and analyze trends, usage, and activities</List.Item>
                  <List.Item>Detect, investigate, and prevent fraudulent transactions and other illegal activities</List.Item>
                </List>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">4. Information Sharing</Title>
                <Text>
                  We do not sell your personal information. We may share your information with:
                </Text>
                <List>
                  <List.Item>
                    <strong>Service Providers:</strong> Third-party vendors who assist in providing our services
                    (e.g., payment processing, email delivery, analytics).
                  </List.Item>
                  <List.Item>
                    <strong>Legal Requirements:</strong> When required by law or to protect our rights and safety.
                  </List.Item>
                  <List.Item>
                    <strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.
                  </List.Item>
                </List>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">5. Third-Party Services</Title>
                <Text mb="xs">Our platform uses the following third-party services:</Text>
                <List>
                  <List.Item>
                    <strong>Stripe:</strong> For payment processing and subscription management.{' '}
                    <Anchor href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
                      View Stripe&apos;s Privacy Policy
                    </Anchor>
                  </List.Item>
                  <List.Item>
                    <strong>Google reCAPTCHA:</strong> For security and bot protection on forms.{' '}
                    <Anchor href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                      View Google&apos;s Privacy Policy
                    </Anchor>
                  </List.Item>
                  <List.Item>
                    <strong>Microsoft Azure:</strong> For cloud hosting, email delivery, and data storage.{' '}
                    <Anchor href="https://privacy.microsoft.com/en-us/privacystatement" target="_blank" rel="noopener noreferrer">
                      View Microsoft&apos;s Privacy Statement
                    </Anchor>
                  </List.Item>
                  <List.Item>
                    <strong>Reddit:</strong> We access publicly available data through Reddit&apos;s API.{' '}
                    <Anchor href="https://www.reddit.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">
                      View Reddit&apos;s Privacy Policy
                    </Anchor>
                  </List.Item>
                  <List.Item>
                    <strong>OpenStreetMap:</strong> We use OpenStreetMap data for business location information.{' '}
                    <Anchor href="https://wiki.osmfoundation.org/wiki/Privacy_Policy" target="_blank" rel="noopener noreferrer">
                      View OSM&apos;s Privacy Policy
                    </Anchor>
                  </List.Item>
                </List>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">6. Cookies and Tracking</Title>
                <Text>
                  We use cookies and similar tracking technologies to track activity on our platform and
                  hold certain information. Cookies are files with a small amount of data which may include
                  an anonymous unique identifier. You can instruct your browser to refuse all cookies or to
                  indicate when a cookie is being sent.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">7. Data Security</Title>
                <Text>
                  We implement appropriate technical and organizational security measures to protect your
                  personal information. However, no method of transmission over the Internet or method of
                  electronic storage is 100% secure.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">8. Your Rights</Title>
                <Text mb="xs">You have the right to:</Text>
                <List>
                  <List.Item>Access the personal information we hold about you</List.Item>
                  <List.Item>Request correction of inaccurate information</List.Item>
                  <List.Item>Request deletion of your personal information</List.Item>
                  <List.Item>Opt out of marketing communications</List.Item>
                  <List.Item>Close your account at any time</List.Item>
                  <List.Item>Request a copy of your data in a portable format</List.Item>
                </List>
                <Text mt="xs">
                  To exercise any of these rights, please contact us at{' '}
                  <Anchor href="mailto:privacy@mentionedme.com">privacy@mentionedme.com</Anchor>.
                  We will respond to your request within 30 days.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">9. Data Retention</Title>
                <Text>
                  We retain your personal information for as long as your account is active or as needed
                  to provide you services. Account data is retained for 30 days after account closure
                  to allow for account recovery. After this period, your data is permanently deleted.
                  Reddit mention data is retained for up to 2 years for historical analytics purposes.
                  You may request earlier deletion of your data at any time.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">10. Geographic Availability</Title>
                <Text>
                  MentionedMe is currently available to businesses located in the United States only.
                  Our services are designed to comply with US federal and state privacy laws.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">11. California Residents (CCPA)</Title>
                <Text mb="xs">
                  If you are a California resident, you have additional rights under the California Consumer
                  Privacy Act (CCPA):
                </Text>
                <List>
                  <List.Item>
                    <strong>Right to Know:</strong> You may request disclosure of the categories and specific
                    pieces of personal information we have collected about you.
                  </List.Item>
                  <List.Item>
                    <strong>Right to Delete:</strong> You may request deletion of your personal information,
                    subject to certain exceptions.
                  </List.Item>
                  <List.Item>
                    <strong>Right to Non-Discrimination:</strong> We will not discriminate against you for
                    exercising your CCPA rights.
                  </List.Item>
                  <List.Item>
                    <strong>No Sale of Personal Information:</strong> We do not sell your personal information
                    to third parties.
                  </List.Item>
                </List>
                <Text mt="xs">
                  To submit a CCPA request, contact us at{' '}
                  <Anchor href="mailto:privacy@mentionedme.com">privacy@mentionedme.com</Anchor>{' '}
                  with the subject line &quot;CCPA Request&quot;.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">12. Children&apos;s Privacy</Title>
                <Text>
                  Our services are not intended for individuals under the age of 18. We do not knowingly
                  collect personal information from children under 18. If you believe we have collected
                  information from a child under 18, please contact us immediately.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">13. International Data Transfers</Title>
                <Text>
                  Your information may be transferred to and processed in the United States and other
                  countries where our service providers operate. We ensure appropriate safeguards are
                  in place for such transfers, including standard contractual clauses approved by
                  relevant data protection authorities.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">14. Changes to This Policy</Title>
                <Text>
                  We may update this Privacy Policy from time to time. We will notify you of any material
                  changes by posting the new Privacy Policy on this page, updating the &quot;Last updated&quot;
                  date, and where appropriate, sending you an email notification. We encourage you to
                  review this Privacy Policy periodically.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">15. Contact Us</Title>
                <Text>
                  If you have any questions about this Privacy Policy or our data practices, please contact us at:
                </Text>
                <List mt="xs">
                  <List.Item>Email: <Anchor href="mailto:privacy@mentionedme.com">privacy@mentionedme.com</Anchor></List.Item>
                  <List.Item>Website: <Anchor href="https://mentionedme.com/contact">mentionedme.com/contact</Anchor></List.Item>
                </List>
              </section>
            </Stack>
          </Stack>
        </Paper>
    </Container>
  );
};

export default PrivacyPage;
